
import { createContext, type FC, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LocaleData } from '@/api/registration';
import { useLocaleListQuery } from '@/registration/hook/useLocaleListQuery';
import { UiSpinner, UiStack } from '@/lib/ui';
import { loadItem, removeItem, saveItem } from '@/api/local';

function getLocaleLocalKey(eventId: string): string {
  return `LOCALE_EVENT_${eventId}`;
}

interface LocaleContextValue {
  currentLocale: LocaleData | null, // This is the current local we should use. It can be null meaning no proper locale is found for this event.
  changeLocale: (eventId: string, locale: LocaleData) => void,
  localeList: LocaleData[],
  isLocal: boolean,
}

// const defaultLocale: LocaleData = {
//   id: 0,
//   code: 'en-AU',
//   languageCode: 'en',
//   countryCode: 'AU',
//   currencyCode: 'AUD',
//   isDefault: true,
// }

const LocaleContext = createContext<LocaleContextValue>({
  currentLocale: null,
  changeLocale: (eventId: string, locale: LocaleData) => {},
  localeList: [],
  isLocal: false,
});

export interface LocaleProviderProps extends PropsWithChildren {
  eventId: string
}

export const LocaleProvider: FC<LocaleProviderProps> = ({
  eventId,
  children,
  ...props
}) => {
  // Get the local locale from the local storage.
  let localLocale = useMemo<LocaleData|null>(() => {
    const localItem = loadItem<LocaleData>({key: getLocaleLocalKey(eventId)}, true);
    if (localItem.item) {
      // Use the access token from the local storage.
      return localItem.item;
    }
    return null;
  }, []);
  const [currentLocale, setCurrentLocale] = useState<LocaleData|null>(null);
  const [localeList, setLocaleList] = useState<LocaleData[]>([]);
  const [isLocal, setIsLocal] = useState<boolean>(false);
  
  // Store the locale in the local storage.
  const persistLocale = useCallback((eventId: string, locale: LocaleData) => {
    saveItem({
      key: getLocaleLocalKey(eventId),
      item: JSON.stringify(locale),
    });
  }, [
    saveItem,
  ]);

  // Fetch the locale from the backend.
  const {data, isLoading} = useLocaleListQuery(eventId);

  // Find the current locale.
  useEffect(() => {
    if (data?.localeList && Array.isArray(data.localeList)) {
      setLocaleList(data?.localeList);

      // Due to whatever reason, no locale is returned from the backend.
      // We do not remove the local locale from the local storage when this happens just in case it's a occasional error.
      if (data.localeList.length === 0) {
        localLocale = null;
        setCurrentLocale(null);
      }

      // Check if the local locale is in the locale list returned from the backend. If not, clear it and remove it from the local storage.
      if (localLocale) {
        const localLocaleIndex = data.localeList.findIndex((lo) => {
          return lo.id === localLocale?.id;
        })
        // Due to whatever reason, the local locale is not the locale list returned from the backend. Clear the local locale from the local storage.
        if (localLocaleIndex === -1) {
          localLocale = null;
          // Remove it from the local storage.
          removeItem({
            key: getLocaleLocalKey(eventId),
          });
        }
      }

      // Set the current locale.
      if (localLocale) {
        // Use the local locale if there is one.
        setCurrentLocale(localLocale);
        setIsLocal(true);
      } else {
        // Use the default locale if no local locale is found.
        for (const lo of data?.localeList) {
          if (lo.isActive) {
            setCurrentLocale(lo);
          }
        }
        setIsLocal(false);
      }
    }
  }, [
    data,
    setIsLocal,
    setLocaleList,
    removeItem,
  ]);

  // Change the locale and store it in the local storage.
  const changeLocale = useCallback((eventId: string, locale: LocaleData) => {
    persistLocale(eventId, locale);
    setCurrentLocale(locale);
  }, [
    setCurrentLocale,
    persistLocale,
  ]);

  return (
    <LocaleContext.Provider
      value={{
        currentLocale,
        changeLocale,
        localeList,
        isLocal,
      }}
      {...props}
    >
      {isLoading ? (
        <UiStack p={8}>
          <UiSpinner size={'lg'} color={'primary.500'} thickness='2px' />
        </UiStack>
      ) : children}
    </LocaleContext.Provider>
  );
};

export function useLocale() {
  const { currentLocale, changeLocale, localeList, isLocal } = useContext(LocaleContext) as Required<LocaleContextValue>;

  return {
    currentLocale,
    changeLocale,
    localeList,
    isLocal,
  };
}
